/**
 * ==========================================
 * خدمة تخزين واسترجاع أكواد الكوبونات في الفواتير
 * ==========================================
 * 
 * هذه الخدمة مسؤولة عن:
 * - تخزين أكواد الكوبونات المطبقة مع الفاتورة
 * - استرجاع الكوبونات حسب معرف الفاتورة
 * - تحديث وحذف سجلات الكوبونات
 * - توفير إحصائيات استخدام الكوبونات
 * 
 * ملاحظة: هذه الخدمة تعمل بشكل مستقل عن النظام الحالي
 * ولا تتطلب أي تعديلات في الملفات الموجودة
 */

import { API_BASE_URL } from '../config';
import {
  InvoiceCouponStorage,
  CouponStorageResult,
  CouponStorageSearchCriteria,
  CouponStorageSearchResponse,
  CouponUsageStatistics,
  InvoiceCouponDetail
} from '../types/couponStorage';

// دالة للحصول على التوكن من localStorage
const getAuthToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('token');
  }
  return null;
};

// دالة لإنشاء headers افتراضية مع التوكن
const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * إنشاء سجل تخزين جديد لأكواد الكوبونات
 */
export const createCouponStorage = async (
  storageData: Omit<InvoiceCouponStorage, 'createdAt' | 'updatedAt' | 'status'>
): Promise<CouponStorageResult> => {
  try {
    const storageRecord: InvoiceCouponStorage = {
      ...storageData,
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const response = await fetch(`${API_BASE_URL}/coupons/storage`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(storageRecord)
    });

    if (!response.ok) {
      throw new Error(`فشل في تخزين الكوبونات: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: true,
      storage: result.data,
      message: 'تم تخزين أكواد الكوبونات بنجاح'
    };
  } catch (error) {
    console.error('خطأ في تخزين الكوبونات:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'حدث خطأ غير معروف'
    };
  }
};

/**
 * تحديث سجل تخزين موجود
 */
export const updateCouponStorage = async (
  invoiceId: string,
  updates: Partial<Omit<InvoiceCouponStorage, 'invoiceId' | 'createdAt'>>
): Promise<CouponStorageResult> => {
  try {
    const response = await fetch(`${API_BASE_URL}/coupons/storage/${invoiceId}`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...updates,
          updatedAt: new Date().toISOString()
        })
      }
    );

    if (!response.ok) {
      throw new Error(`فشل في تحديث الكوبونات: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: true,
      storage: result.data,
      message: 'تم تحديث أكواد الكوبونات بنجاح'
    };
  } catch (error) {
    console.error('خطأ في تحديث الكوبونات:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'حدث خطأ غير معروف'
    };
  }
};

/**
 * استرجاع سجل تخزين حسب معرف الفاتورة
 */
export const getCouponStorageByInvoiceId = async (
  invoiceId: string
): Promise<CouponStorageResult> => {
  try {
    const response = await fetch(`${API_BASE_URL}/coupons/storage/invoice/${invoiceId}`,
      {
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          error: 'لا توجد سجلات كوبونات لهذه الفاتورة'
        };
      }
      throw new Error(`فشل في استرجاع الكوبونات: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: true,
      storage: result.data,
      message: 'تم استرجاع بيانات الكوبونات بنجاح'
    };
  } catch (error) {
    console.error('خطأ في استرجاع الكوبونات:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'حدث خطأ غير معروف'
    };
  }
};

/**
 * البحث عن سجلات الكوبونات بمعايير متقدمة
 */
export const searchCouponStorages = async (
  criteria: CouponStorageSearchCriteria
): Promise<CouponStorageSearchResponse> => {
  try {
    const params = new URLSearchParams();
    
    if (criteria.invoiceId) params.append('invoiceId', criteria.invoiceId);
    if (criteria.invoiceNumber) params.append('invoiceNumber', criteria.invoiceNumber);
    if (criteria.couponCode) params.append('couponCode', criteria.couponCode);
    if (criteria.couponCodes) criteria.couponCodes.forEach(code => params.append('couponCodes', code));
    if (criteria.startDate) params.append('startDate', criteria.startDate);
    if (criteria.endDate) params.append('endDate', criteria.endDate);
    if (criteria.branchId) params.append('branchId', criteria.branchId);
    if (criteria.status) params.append('status', criteria.status);
    if (criteria.skip) params.append('skip', criteria.skip.toString());
    if (criteria.limit) params.append('limit', criteria.limit.toString());

    const response = await fetch(`${API_BASE_URL}/coupons/storage/search?${params.toString()}`,
      {
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`فشل في البحث: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('خطأ في البحث عن الكوبونات:', error);
    return {
      success: false,
      data: {
        storages: [],
        total: 0,
        page: 1,
        pages: 1
      },
      message: error instanceof Error ? error.message : 'حدث خطأ في البحث'
    };
  }
};

/**
 * حذف سجل تخزين (إلغاء)
 */
export const cancelCouponStorage = async (
  invoiceId: string
): Promise<CouponStorageResult> => {
  try {
    const response = await fetch(`${API_BASE_URL}/coupons/storage/${invoiceId}/cancel`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status: 'cancelled',
          updatedAt: new Date().toISOString()
        })
      }
    );

    if (!response.ok) {
      throw new Error(`فشل في إلغاء الكوبونات: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: true,
      storage: result.data,
      message: 'تم إلغاء الكوبونات بنجاح'
    };
  } catch (error) {
    console.error('خطأ في إلغاء الكوبونات:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'حدث خطأ غير معروف'
    };
  }
};

/**
 * إضافة كوبون جديد إلى سجل تخزين موجود
 */
export const addCouponToStorage = async (
  invoiceId: string,
  couponData: InvoiceCouponDetail
): Promise<CouponStorageResult> => {
  try {
    const response = await fetch(`${API_BASE_URL}/coupons/storage/${invoiceId}/add`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(couponData)
      }
    );

    if (!response.ok) {
      throw new Error(`فشل في إضافة الكوبون: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: true,
      storage: result.data,
      message: 'تم إضافة الكوبون بنجاح'
    };
  } catch (error) {
    console.error('خطأ في إضافة الكوبون:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'حدث خطأ غير معروف'
    };
  }
};

/**
 * إزالة كوبون من سجل تخزين موجود
 */
export const removeCouponFromStorage = async (
  invoiceId: string,
  couponCode: string
): Promise<CouponStorageResult> => {
  try {
    const response = await fetch(`${API_BASE_URL}/coupons/storage/${invoiceId}/remove/${couponCode}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`فشل في إزالة الكوبون: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: true,
      storage: result.data,
      message: 'تم إزالة الكوبون بنجاح'
    };
  } catch (error) {
    console.error('خطأ في إزالة الكوبون:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'حدث خطأ غير معروف'
    };
  }
};

/**
 * الحصول على إحصائيات استخدام الكوبونات
 */
export const getCouponUsageStatistics = async (): Promise<{
  success: boolean;
  data?: CouponUsageStatistics;
  message?: string;
}> => {
  try {
    const response = await fetch(`${API_BASE_URL}/coupons/storage/statistics`,
      {
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`فشل في الحصول على الإحصائيات: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('خطأ في الحصول على الإحصائيات:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'حدث خطأ في الحصول على الإحصائيات'
    };
  }
};
